<template>
  <div>
    <player-breadcrumb
      class="mb-2"
      :view="$t('player_profile.attendance')"
      :player="player"
    >
      <v-row
        align="center"
        dense
      >
        <v-spacer />
        <v-col
          cols="auto"
          class="text-subtitle-1 grey--text pr-2"
        >
          {{ $t('dropdown.select_month.title') }}
        </v-col>
        <v-col cols="auto">
          <v-select
            v-model="selectedMonth"
            :items="months"
            solo
            hide-details
            style="width: 400px"
            item-value="text"
            item-text="text"
            return-object
            @change="fetchEvents()"
          />
        </v-col>
        <v-col
          cols="auto"
          class="pl-2"
        >
          <v-btn
            text
            class="text-none"
            target="_blank"
            :href="downloadHref"
          >
            <v-icon
              left
              small
            >
              fal fa-download
            </v-icon>
            {{ $t('button.download_report') }}
          </v-btn>
        </v-col>
      </v-row>
    </player-breadcrumb>
    <v-row
      v-if="playerStatsToday && playerStatsToday.stats && playerStatsToday.stats.event_attendance"
      class="my-2"
    >
      <v-col>
        <stat-card
          :title="$t('card_title.attendance_this_week')"
          :value-text="playerStatsToday.stats.event_attendance.week.percentage ? (playerStatsToday.stats.event_attendance.week.percentage * 100).toFixed(0) + '%' : undefined"
          :value="playerStatsToday.stats.event_attendance.week.percentage ? playerStatsToday.stats.event_attendance.week.percentage * 100 : undefined"
          visual
          :max="100"
        />
      </v-col>
      <v-col>
        <stat-card
          :title="$t('card_title.this_week_missed_events')"
          :value="playerStatsToday.stats.event_attendance.week.not_attended"
          visual
        />
      </v-col>
      <v-col>
        <stat-card
          :title="$t('card_title.attendance_this_month')"
          :value-text="playerStatsToday.stats.event_attendance.month.percentage ? (playerStatsToday.stats.event_attendance.month.percentage * 100).toFixed(0) + '%' : undefined"
          :value="playerStatsToday.stats.event_attendance.month.percentage ? playerStatsToday.stats.event_attendance.month.percentage * 100 : undefined"
          visual
          :max="100"
        />
      </v-col>
      <v-col>
        <stat-card
          :title="$t('card_title.attendance_this_year')"
          :value-text="playerStatsToday.stats.event_attendance.year.percentage ? (playerStatsToday.stats.event_attendance.year.percentage * 100).toFixed(0) + '%' : undefined"
          :value="playerStatsToday.stats.event_attendance.year.percentage ? playerStatsToday.stats.event_attendance.year.percentage * 100 : undefined"
          visual
          :max="100"
        />
      </v-col>
    </v-row>
    <div class="mb-10">
      <v-data-table
        :headers="tableHeaders"
        :items="events.data"
        hide-default-footer
        :loading="events.loading"
        :items-per-page="events.pagination.per_page"
        disable-sort
      >
        <template
          v-slot:item="{ item: event }"
        >
          <tr>
            <td>
              {{ event.title }}
            </td>
            <td style="width: 20%">
              <v-chip
                label
                :color="eventTypes[event.event_type].color"
                dark
                small
              >
                {{ eventTypes[event.event_type].text }}
              </v-chip>
            </td>
            <td style="width: 25%">
              {{ $dayjs(event.start_at).format('ddd, ll HH:mm:ss') }}
            </td>
            <td style="width: 20%">
              <v-chip
                v-if="event.has_attended"
                label
                small
                dark
                color="success"
              >
                {{ $t('status.yes') }}
              </v-chip>
              <v-chip
                v-else-if="event.has_attended === false"
                label
                small
                dark
                color="error"
              >
                {{ $t('status.no') }}
              </v-chip>
              <v-chip
                v-else
                label
                small
                color="grey lighten-1"
                dark
              >
                N/A
              </v-chip>
            </td>
          </tr>
        </template>
      </v-data-table>
      <div
        v-if="events.pagination && events.pagination.last_page > 1"
        class="d-flex justify-center align-center py-5"
      >
        <pagination
          v-model="events.pagination.current_page"
          :last-page="events.pagination.last_page"
          @input="fetchEvents()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import StatCard from '@/components/StatCard.vue'
import { PlayerApiService } from '@/data/services'
import dayjs from 'dayjs'
import { EventType, EventTypes } from '@/data/models'
import Pagination from '@/components/Pagination.vue'
import i18n from '@/plugins/i18n'
import { processMaxValues } from '@/utils/helpers'
import PlayerBreadcrumb from '@/components/players/PlayerBreadcrumb.vue'

export default {
  name: 'PlayerEventAttendanceView',
  components: { PlayerBreadcrumb, Pagination, StatCard },
  props: {
  },
  data() {
    return {
      events: {
        loading: false,
        data: [],
        pagination: {
          current_page: 1,
          last_page: undefined,
          per_page: 25
        }
      },
      playerStats: {
        data: [],
        pagination: {
          current_page: 1,
          last_page: 1,
          per_page: 1
        },
        maxValues: {},
        loading: false
      },
      player: {
        loading: false,
        data: {}
      },
      months: [],
      selectedMonth: undefined
    }
  },
  computed: {
    downloadHref() {
      let link = process.env.VUE_APP_API_URL + `/players/${this.$route.params.id}/event-attendance-report`

      if (this.selectedMonth) {
        link = link + `?from=${this.selectedMonth.start}&to=${this.selectedMonth.end}`
      }

      return link
    },
    playerStatsToday() {
      return this.playerStats.data[0]
    }
  },
  created() {
    const dates = []

    for (let i = 0; i < 12; i++) {
      const date = dayjs().set('month', dayjs().get('month') - i)

      const dateObject = {
        text: date.format('MM/YYYY'),
        start: date.startOf('month').format('YYYY-MM-DD'),
        end: date.endOf('month').format('YYYY-MM-DD')
      }

      dates.push(dateObject)

      if (date.get('month') === dayjs().get('month')) {
        this.selectedMonth = dateObject
      }
    }

    this.months = dates

    this.tableHeaders = [
      { value: 'title', text: i18n.t('event.label.title'), sortable: false },
      { value: 'event_type', text: i18n.t('training_builder.configure.dropdown.type.title') },
      { value: 'start_at', text: i18n.t('picker.date.title') },
      { value: 'has_attended', text: i18n.t('attended.label') }
    ]

    this.fetchEvents()
    this.fetchPlayerDetails()
    this.fetchPlayerStats()

    this.eventTypes = EventTypes
  },
  methods: {
    async fetchPlayerDetails() {
      try {
        this.player.loading = true

        const resp = await PlayerApiService.getPlayerDetails(this.$route.params.id)
        this.player.data = resp.data
      } catch (e) {
        // todo
      } finally {
        this.player.loading = false
      }
    },
    async fetchEvents() {
      try {
        this.events.loading = true

        const queryParams = {
          page: this.events.pagination.current_page,
          per_page: this.events.pagination.per_page,
          from: this.selectedMonth.start,
          to: this.selectedMonth.end,
          types: [EventType.TRAINING, EventType.MATCH, EventType.TEST_SESSION]
        }

        const resp = await PlayerApiService.getEvents(this.$route.params.id, queryParams)

        this.events.data = resp.data
        this.events.pagination = resp.meta
      } catch (e) {
        this.events.data = []
        this.events.pagination.current_page = 1
        this.events.pagination.last_page = 1
      } finally {
        this.events.loading = false
      }
    },
    async fetchPlayerStats() {
      try {
        this.playerStats.loading = true

        const queryParams = {
          page: this.playerStats.pagination.current_page,
          per_page: this.playerStats.pagination.per_page
        }

        if (this.dateRange) {
          queryParams.dateTo = dayjs(this.dateRange.to).format('YYYY-MM-DD')
          queryParams.dateFrom = dayjs(this.dateRange.from).format('YYYY-MM-DD')
        }

        const resp = await PlayerApiService.getStats(this.$route.params.id, queryParams)

        this.playerStats.maxValues = processMaxValues(resp.data.map(stat => stat.stats))

        this.playerStats.data = resp.data.map(stat => {
          if (!stat.stats) {
            return stat
          }

          if (stat.stats.SLD) stat.stats.SLD = (stat.stats.SLD / 60).toFixed(2)

          if (stat.stats.FAT) stat.stats.FAT_perc = (((6 - stat.stats.FAT) / 5) * 100).toFixed(0)
          if (stat.stats.GMS) stat.stats.GMS_perc = (((6 - stat.stats.GMS) / 5) * 100).toFixed(0)
          if (stat.stats.MOO) stat.stats.MOO_perc = ((stat.stats.MOO / 5) * 100).toFixed(0)
          if (stat.stats.STL) stat.stats.STL_perc = (((6 - stat.stats.STL) / 5) * 100).toFixed(0)

          return stat
        })
        this.playerStats.pagination = resp.meta
      } catch (e) {
        // todo
      } finally {
        this.playerStats.loading = false
      }
    }
  }
}
</script>

<style scoped>

</style>
